/**
 * Component: Accordion
 * ------------------------------------------------------------------------------
 * Standard Flickity powered Carousel with options.
 *
 * @namespace Accordion
 */

import log from 'salvo-lite/log'
import { on } from '../helpers/utils'
import cssClasses from '../helpers/cssClasses'

class Accordion {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            selectors: {
                content: '[data-accordion-content]',
                control: '[data-accordion-control]',
                item: '[data-accordion-item]',
                scrollTo: '[data-accordion-scroll]'
            },
            aria: true,
            singleOpen: true,
            showInitial: false,
            ...JSON.parse(elem.getAttribute('data-accordion') || '{}')
        }
        this._items = [...this._elem.querySelectorAll(this._settings.selectors.item)]
        this._controls = [...this._elem.querySelectorAll(this._settings.selectors.control)]
        this._scrollToLinks = [...document.querySelectorAll(this._settings.selectors.scrollTo)]

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise accordion component.
     */
    onInit () {
        this._setARIA()
        this._setClickEvents()
    }

    /**
     * Set click events on items.
     */
    _setClickEvents () {
        this._controls.forEach((item) => {
            on('click', item, (event) => this._onItemClick(event, item.parentNode))
        })

        this._scrollToLinks.forEach((item) => {
            on('click', item, (event) => this._scrollToTab(event.target))
        })
    }

    _scrollToTab (element) {
        const ACCORDION_CONTAINER_POSITION = document.querySelector('.product-tabs__accordions').getBoundingClientRect().top
        const ACCORDION_ID = element.closest('[data-accordion-scroll]').getAttribute('data-accordion-scroll')
        const ACCORDION_ELEMENT = document.querySelector(`[data-accordion-id="${ACCORDION_ID}"]`).querySelector('.accordion__item')

        window.scrollBy({
            top: ACCORDION_CONTAINER_POSITION - 160,
            behavior: 'smooth'
        })

        if (!ACCORDION_ELEMENT.classList.contains(cssClasses.active)) {
            this._toggleElement(ACCORDION_ELEMENT)
        }
    }

    /**
    * Handle toggle event on accordion control click.
    * @param {Event} event - Click event.
    * @param {HTMLElement} element - The accordion item.
    */
    _onItemClick (event, element) {
        event.preventDefault()
        this._toggleElement(element)
    }

    /**
     * Toggle targeted item.
     * @param {HTMLElement} element - The accordion item.
     */
    _toggleElement (element) {
        if (this._settings.singleOpen) {
            this._hideUnselected(element)
        }

        if (this._isItemActive(element)) {
            this._hideItem(element)
            return
        }

        this._showItem(element)
    }

    /**
   * Create ARIA attributes.
   */
    _setARIA () {
        this._elem.setAttribute('role', 'tablist')
        this._elem.setAttribute('aria-label', 'Accordion Control Group Buttons')
        this._items.forEach((item) => {
            if (this._isItemActive(item)) {
                this._updateARIA(item, true)
                return
            }
            this._updateARIA(item, false)
        })
    }

    /**
     * Update ARIA.
     * @param {HTMLElement} element - The accordion item.
     * @param {Boolean} value - Aria value.
     */
    _updateARIA (element, value) {
        element.setAttribute('aria-selected', value)
        element.querySelector(this._settings.selectors.control).setAttribute('aria-expanded', value)
        element.querySelector(this._settings.selectors.content).setAttribute('aria-hidden', !value)

        if (!this._itemHasContentId(element)) {
            this._updateARIAContentId(element)
        }
    }

    /**
     * Check if ARIA control contains content id.
     * @param {HTMLElement} element - The accordion item.
     */
    _itemHasContentId (element) {
        return element.querySelector(this._settings.selectors.control).hasAttribute('aria-controls')
    }

    /**
     * Update ARIA content id.
     * @param {HTMLElement} element - Click The accordion item.
     */
    _updateARIAContentId (element) {
        const contentId = element.querySelector(this._settings.selectors.content).id
        element.querySelector(this._settings.selectors.control).setAttribute('aria-controls', contentId)
    }

    /**
     * Show targeted item and set ARIA values.
     * @param {HTMLElement} element - The accordion item.
     */
    _showItem (element) {
        element.classList.add(cssClasses.active)

        if (this._settings.aria) {
            this._updateARIA(element, true)
        }

        this._dataLayer(element, 'Accordion Opened')
    }

    /**
     * Hide targeted item and set ARIA values.
     * @param {HTMLElement} element - The accordion item.
     */
    _hideItem (element) {
        element.classList.remove(cssClasses.active)

        if (this._settings.aria) {
            this._updateARIA(element, false)
        }

        this._dataLayer(element, 'Accordion Closed')
    }

    /**
     * Check if item is active.
     * @param {HTMLElement} element - The accordion item.
     */
    _isItemActive (element) {
        return element.classList.contains(cssClasses.active)
    }

    /**
     * Hide all unselected items.
     * @param {HTMLElement} element - The accordion item.
     */
    _hideUnselected (element) {
        this._items.forEach((item) => {
            if (this._isItemActive(item) && item !== element) {
                this._hideItem(item)
            }
        })
    }

    /**
     * Hide all items.
     */
    _hideAllItems () {
        this._items.forEach((item) => {
            this._hideItem(item)
        })
    }

    /**
     * Show all items.
     */
    _showAllItems () {
        this._items.forEach((item) => {
            this._showItem(item)
        })
    }

    /**
     * Send accordion title and state data to the DataLayer.
    */
    _dataLayer (element, accordionAction) {
        const accordionTitle = element.querySelector('button').innerText

        window.dataLayer.push({
            accordionTitle,
            accordionAction
        })
    }

    /**
   * Expose public interface.
   */
    // return Object.freeze({
    //     init,
    //     hideAllItems,
    //     showAllItems,
    //     hideItem,
    //     showItem,
    // })
}

export default Accordion



// WEBPACK FOOTER //
// ./src/resources/scripts/components/Accordion.js